import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, Sparkles, Loader2, RefreshCw, AlertCircle, Clock } from 'lucide-react';
import { billingApi } from '../lib/billingApi';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../lib/i18n';
import { isStoreApp, isCapacitorNative } from '../lib/platform';

const ALLOWED_STATUS = ['active', 'trialing'];
const CACHE_KEY = 'sp.lastSubStatus.v1';
const IAP_GRACE_KEY = 'sp.iapGraceUntil.v1';
// 🍎 Flag PERSISTANT (jamais effacé) — une fois qu'un user a réussi à
// acheter ou restaurer un abonnement Apple IAP, on ne doit JAMAIS plus
// jamais lui réafficher l'écran "essai gratuit terminé". Si le backend
// est en retard ou dit le contraire, on affiche un écran neutre de
// synchronisation, jamais le paywall expiré. Exigence explicite de
// Shayla — éviter une UX catastrophique post-achat sur TestFlight/prod.
const IAP_PURCHASED_KEY = 'sp.iapPurchased.v1';
// Cached status is trusted for 30 days offline — long enough to cover a normal
// review cycle (Apple, vacation, travel) without bothering the user, but short
// enough that a truly cancelled account can't bypass the paywall forever.
const CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
// 🍎 Apple IAP grace period: after a successful purchase, we TRUST the local
// cache for 5 minutes regardless of what the backend says. The RevenueCat→
// Supabase webhook can lag, and we MUST NOT show "trial expired" to a user
// who just paid (catastrophic UX, certain Apple rejection).
const IAP_GRACE_MS = 5 * 60 * 1000;

function isInIapGracePeriod() {
  try {
    const raw = localStorage.getItem(IAP_GRACE_KEY);
    if (!raw) return false;
    return Number(raw) > Date.now();
  } catch { return false; }
}

function hasEverPurchased() {
  try {
    return localStorage.getItem(IAP_PURCHASED_KEY) !== null;
  } catch { return false; }
}

const STR = {
  fr: {
    past_due_title:         "Paiement en échec",
    past_due_desc:          "Mets à jour ton moyen de paiement pour récupérer l'accès à ton compte.",
    past_due_btn:           "Mettre à jour mon paiement",
    canceled_title:         "Abonnement terminé",
    canceled_desc:          "Ton abonnement a pris fin. Réactive-le pour reprendre tes soumissions.",
    canceled_btn:           "Réactiver mon abonnement",
    pending_title:          "Paiement en cours de traitement",
    pending_desc:           "Si tu viens de compléter ton paiement, clique sur « Rafraîchir » pour synchroniser ton abonnement. Sinon, choisis un plan pour démarrer.",
    pending_btn:            "Voir les plans",
    expired_title:          "Ton essai gratuit est terminé",
    expired_desc:           "Les 3 jours d'essai sont écoulés. Souscris à un plan mensuel ou annuel pour continuer à créer tes soumissions, signer tes contrats et utiliser le catalogue de matériaux.",
    expired_btn:            "Choisir mon plan",
    syncing_title:          "Synchronisation de ton abonnement…",
    syncing_desc:           "Ton paiement a été reçu. Apple est en train de confirmer ton achat. Touche « Rafraîchir » dans un instant si l'app ne se débloque pas automatiquement.",
    free_trial_badge:       "ESSAI GRATUIT — 3 JOURS",
    expired_badge:          "ESSAI TERMINÉ",
    activate_title:         "Active ton compte pour continuer",
    activate_desc:          "Démarre ton essai gratuit de 3 jours. Toutes les fonctionnalités déverrouillées, annule à tout moment.",
    activate_btn:           "Démarrer mon essai gratuit",
    sync:                   "Synchronisation…",
    already_paid:           "J'ai déjà payé — Rafraîchir",
    go_profile:             "Aller à mon profil",
    iap_disclosure:         "Abonnement auto-renouvelable. Le paiement sera porté à ton identifiant Apple. Se renouvelle automatiquement sauf annulation 24h avant la fin de la période. Gère depuis Réglages iOS.",
    legal_privacy:          "Politique de confidentialité",
    legal_terms:            "Conditions d'utilisation (EULA)",
  },
  en: {
    past_due_title:         "Payment failed",
    past_due_desc:          "Update your payment method to restore access to your account.",
    past_due_btn:           "Update payment",
    canceled_title:         "Subscription ended",
    canceled_desc:          "Your subscription has ended. Reactivate it to resume creating quotes.",
    canceled_btn:           "Reactivate subscription",
    pending_title:          "Payment is being processed",
    pending_desc:           "If you just completed your payment, click \"Refresh\" to sync your subscription. Otherwise, choose a plan to get started.",
    pending_btn:            "View plans",
    expired_title:          "Your free trial has ended",
    expired_desc:           "The 3-day free trial is over. Subscribe to a monthly or annual plan to keep creating quotes, signing contracts, and using the materials catalog.",
    expired_btn:            "Choose my plan",
    syncing_title:          "Syncing your subscription…",
    syncing_desc:           "Your payment was received. Apple is confirming your purchase. Tap \"Refresh\" in a moment if the app doesn't unlock automatically.",
    free_trial_badge:       "FREE TRIAL — 3 DAYS",
    expired_badge:          "TRIAL ENDED",
    activate_title:         "Activate your account to continue",
    activate_desc:          "Start your 3-day free trial. All features unlocked, cancel anytime.",
    activate_btn:           "Start free trial",
    sync:                   "Syncing…",
    already_paid:           "I already paid — Refresh",
    go_profile:             "Go to my profile",
    iap_disclosure:         "Auto-renewable subscription. Payment will be charged to your Apple ID. Renews automatically unless canceled at least 24 hours before the end of the period. Manage in iOS Settings.",
    legal_privacy:          "Privacy Policy",
    legal_terms:            "Terms of Use (EULA)",
  },
};

/** Persist the last KNOWN good status so we can boot offline without bothering the user. */
function cacheStatus(s) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ status: s, ts: Date.now() }));
  } catch { /* localStorage disabled / quota — safe to ignore */ }
}

function readCachedStatus() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { status, ts } = JSON.parse(raw);
    if (!status || !ts) return null;
    if (Date.now() - ts > CACHE_MAX_AGE_MS) return null;
    return status;
  } catch {
    return null;
  }
}

/**
 * SubscriptionGate — bloque l'accès aux pages protégées si l'utilisateur
 * n'a pas d'abonnement actif ou en essai. Affiche un message contextuel.
 *
 * OFFLINE-FIRST: Si le navigateur est hors-ligne ou que la requête échoue,
 * on s'appuie sur le dernier statut connu (cache localStorage) au lieu de
 * bloquer l'app. Indispensable pour les chantiers sans réseau et pour
 * passer la vérification Apple (mode avion).
 */
export function SubscriptionGate({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useT();
  const T = STR[lang === 'en' ? 'en' : 'fr'];
  const [status, setStatus] = useState(() => readCachedStatus());
  const [refreshing, setRefreshing] = useState(false);

  const loadStatus = useCallback(async ({ silent = false } = {}) => {
    if (!user) return;
    // 🍎 Grace period IAP: après un achat Apple récent (< 5min), on FAIT
    // CONFIANCE au cache local et on n'écrase JAMAIS avec le backend (qui
    // peut être en retard de plusieurs minutes via webhook RevenueCat).
    // Sans ça, le user serait remis sur le paywall après son achat = REJET.
    if (isInIapGracePeriod()) {
      const cached = readCachedStatus();
      if (cached) {
        setStatus(cached);
        if (!silent) setRefreshing(false);
        return;
      }
    }
    if (!navigator.onLine) {
      // Hors-ligne: on s'en tient au cache (déjà chargé dans le state initial).
      if (!silent) setRefreshing(false);
      return;
    }
    // 🔁 Retry avec back-off généreux pour absorber:
    //   - les cold-starts du backend (jusqu'à 30s)
    //   - le délai de propagation du token Supabase après Sign in with Apple natif
    //   - tout autre hoquet réseau temporaire
    // Pendant toute cette opération, on N'AFFICHE RIEN à l'utilisateur:
    // il est déjà DANS l'app (fail-open par défaut). Aucun écran "vérification".
    const delays = [0, 1000, 3000, 7000, 15000]; // 5 essais sur ~26s
    for (let i = 0; i < delays.length; i++) {
      if (delays[i] > 0) await new Promise((r) => setTimeout(r, delays[i]));
      try {
        const s = await billingApi.status();
        setStatus(s);
        if (ALLOWED_STATUS.includes(s?.status)) cacheStatus(s);
        if (!silent) setRefreshing(false);
        return;
      } catch {
        // continue retry loop
      }
    }
    // Tous les essais ont échoué → on garde le statut courant (cache ou null),
    // on N'AFFICHE PAS d'erreur, on continue à laisser passer l'utilisateur.
    // Une nouvelle tentative se fera au retour réseau (listener 'online').
    if (!silent) setRefreshing(false);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    // Lancement de la vérification en arrière-plan, jamais bloquant.
    loadStatus({ silent: true });
  }, [user, loadStatus]);

  // Re-check status whenever the network comes back online.
  useEffect(() => {
    const up = () => loadStatus({ silent: true });
    window.addEventListener('online', up);
    return () => window.removeEventListener('online', up);
  }, [loadStatus]);

  // 🍎 Re-check status on every route change. Indispensable pour que le
  // paywall disparaisse INSTANTANÉMENT après un achat Apple IAP réussi:
  // Pricing.jsx fait `navigate('/')` → useLocation change → on re-fetch
  // le status → si le webhook RevenueCat a déjà mis Supabase à jour, le
  // user voit le Dashboard. Sans ça, le user reste bloqué sur l'écran
  // "Essai terminé" même s'il a payé.
  useEffect(() => {
    if (!user) return;
    loadStatus({ silent: true });
  }, [location.pathname, user, loadStatus]);

  // 🛒 Custom event "subscription:refresh" — déclenché par Pricing.jsx
  // après un achat IAP ou un Restore Purchases réussi. Force un re-fetch
  // immédiat du status pour débloquer l'utilisateur sans page reload.
  useEffect(() => {
    const onRefresh = () => loadStatus({ silent: true });
    window.addEventListener('subscription:refresh', onRefresh);
    return () => window.removeEventListener('subscription:refresh', onRefresh);
  }, [loadStatus]);

  // 🛡️ Auto-restore background — toutes les 5 minutes, SI l'utilisateur a
  // déjà acheté (sp.iapPurchased.v1 posé) ET que le backend ne reconnaît
  // PAS encore son abonnement (status pas dans ALLOWED_STATUS), on déclenche
  // automatiquement :
  //   1) Un appel à RevenueCat Purchases.restorePurchases() qui re-synchronise
  //      les receipts Apple côté RevenueCat → re-fire le webhook → upsert
  //      la subscription dans Supabase.
  //   2) Un refetch immédiat du backend status.
  // Couvre le cas RARISSIME où le webhook RevenueCat n'a JAMAIS fire
  // (panne RevenueCat, mauvaise config, etc.). Avec ça, l'app se débloque
  // toute seule en max 5 min, sans aucune action de l'utilisateur.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const tryAutoRestore = async () => {
      if (cancelled) return;
      // Skip si l'app n'est pas en avant-plan, ou hors-ligne, ou déjà active
      if (!navigator.onLine) return;
      if (document.visibilityState === 'hidden') return;
      if (!hasEverPurchased()) return;
      if (ALLOWED_STATUS.includes(status?.status)) return;
      try {
        // 1) Re-trigger RevenueCat sync sur native iOS (no-op sur web)
        if (isCapacitorNative()) {
          try {
            const { Purchases } = await import('@revenuecat/purchases-capacitor');
            await Purchases.restorePurchases();
          } catch (e) {
            // Pas grave — RevenueCat peut être indispo, on retombe sur le
            // backend qui acceptera le X-IAP-Recent header de toute façon.
            console.debug('[gate] auto-restore RC failed:', e?.message);
          }
        }
        // 2) Re-fetch backend status (avec le header X-IAP-Recent qui sera
        // automatiquement ajouté par billingApi si on est dans la fenêtre).
        if (!cancelled) loadStatus({ silent: true });
      } catch (e) {
        console.debug('[gate] auto-restore failed:', e?.message);
      }
    };
    // Première tentative après 30 secondes (laisse le temps au webhook
    // RevenueCat de fire après le 1er purchase). Ensuite, toutes les 5 min.
    const t0 = setTimeout(tryAutoRestore, 30_000);
    const interval = setInterval(tryAutoRestore, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearTimeout(t0);
      clearInterval(interval);
    };
  }, [user, status?.status, loadStatus]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadStatus();
  };

  // 🟢 PRINCIPE FONDAMENTAL: on ne bloque JAMAIS l'utilisateur tant qu'on n'a
  // pas une réponse CONFIRMÉE négative du backend. Pas de spinner, pas
  // d'écran d'erreur réseau, pas de "vérification de ton abonnement". L'app
  // s'ouvre instantanément. Le paywall n'apparaît QUE si le backend renvoie
  // un statut explicitement non-autorisé (canceled, past_due, expired, etc.).
  if (!user) return children;
  if (!status) return children; // 1er rendu / encore en train de vérifier en arrière-plan
  if (ALLOWED_STATUS.includes(status.status)) return children;

  const s = status?.status;

  // 🍎 Si l'utilisateur a DÉJÀ effectué un achat avec succès (flag persistant
  // posé par Pricing.jsx après purchase ou restore), on N'AFFICHE JAMAIS
  // l'écran "essai gratuit terminé". À la place, on montre un écran neutre
  // de "synchronisation en cours" + bouton Rafraîchir. Indispensable: après
  // un achat IAP réussi, le webhook RevenueCat→Supabase peut prendre du
  // temps (ou échouer ponctuellement), et un user qui vient de PAYER ne
  // doit jamais revoir un message qui suggère qu'il n'a pas accès.
  const everPurchased = hasEverPurchased();

  // Determine context: expired auto-trial / cancelled / past_due / never started / pending
  const autoTrialExpired = status?.auto_trial_expired === true;
  const hadStripeAttempt = status?.had_stripe_attempt === true;
  const isCanceled = s === 'canceled';
  const isPastDue = s === 'past_due' || s === 'unpaid';
  const isPendingCheckout = s === 'pending_checkout' || (s === 'none' && hadStripeAttempt);
  // Mode "synchronisation" — on l'affiche au lieu du paywall expiré quand
  // un achat passé existe en local mais que le backend n'a pas (encore)
  // été mis à jour. Pas pour past_due/canceled qui sont des états distincts
  // et légitimes après un échec de renouvellement ou une annulation.
  const isSyncing = everPurchased && !isPastDue && !isCanceled;

  let title;
  let description;
  let icon;
  let iconBg = '#7A1F2B';
  let iconColor = '#C9A961';
  let primaryBtnLabel = T.pending_btn;
  let showRefresh = false;

  if (isPastDue) {
    icon = <AlertCircle className="w-8 h-8" />;
    iconBg = '#B91C1C';
    iconColor = '#FFF';
    title = T.past_due_title;
    description = T.past_due_desc;
    primaryBtnLabel = T.past_due_btn;
  } else if (isCanceled) {
    icon = <Lock className="w-8 h-8" />;
    title = T.canceled_title;
    description = T.canceled_desc;
    primaryBtnLabel = T.canceled_btn;
  } else if (isSyncing) {
    // 🍎 User a déjà acheté → JAMAIS d'écran "essai expiré".
    // On affiche un écran neutre de synchronisation, on propose un
    // bouton Rafraîchir, et on déclenche un re-fetch silencieux.
    icon = <Loader2 className="w-8 h-8 animate-spin" />;
    iconBg = '#C9A961';
    iconColor = '#FFF';
    title = T.syncing_title;
    description = T.syncing_desc;
    primaryBtnLabel = T.already_paid;
    showRefresh = false; // le bouton primaire EST déjà "Rafraîchir"
  } else if (autoTrialExpired) {
    // ⚠️ DOIT être AVANT isPendingCheckout — sinon un user dont l'essai est
    // terminé ET qui n'a JAMAIS lancé de checkout (status='pending_checkout'
    // côté backend par défaut) verrait à tort le message "Paiement en cours".
    icon = <Clock className="w-8 h-8" />;
    iconBg = '#7A1F2B';
    iconColor = '#C9A961';
    title = T.expired_title;
    description = T.expired_desc;
    primaryBtnLabel = T.expired_btn;
  } else if (isPendingCheckout) {
    icon = <Clock className="w-8 h-8" />;
    iconBg = '#C9A961';
    iconColor = '#FFF';
    title = T.pending_title;
    description = T.pending_desc;
    primaryBtnLabel = T.pending_btn;
    showRefresh = true;
  } else {
    icon = <Sparkles className="w-8 h-8" />;
    iconBg = '#7A1F2B';
    iconColor = '#C9A961';
    title = T.activate_title;
    description = T.activate_desc;
    primaryBtnLabel = T.activate_btn;
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{ backgroundColor: '#FAF7F2' }}
      data-testid="subscription-gate"
    >
      <div
        className="max-w-md w-full bg-white rounded-2xl p-8 text-center border-2"
        style={{ borderColor: '#C9A961' }}
      >
        <div
          className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center"
          style={{ backgroundColor: iconBg, color: iconColor }}
        >
          {icon}
        </div>

        {!isPastDue && !isCanceled && !isPendingCheckout && !autoTrialExpired && !isSyncing && (
          <div
            className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest mb-2"
            style={{ color: '#C9A961' }}
          >
            <Sparkles className="w-3 h-3" />
            {T.free_trial_badge}
          </div>
        )}

        {autoTrialExpired && !isSyncing && (
          <div
            className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest mb-2"
            style={{ color: '#7A1F2B' }}
            data-testid="trial-expired-badge"
          >
            <Clock className="w-3 h-3" />
            {T.expired_badge}
          </div>
        )}

        <h1 className="text-2xl font-bold mb-3" style={{ color: '#5A1620' }}>
          {title}
        </h1>

        <p className="text-base mb-6" style={{ color: '#4A5568' }}>
          {description}
        </p>

        <button
          onClick={isSyncing ? handleRefresh : () => navigate('/pricing')}
          disabled={isSyncing && refreshing}
          className="w-full py-3.5 rounded-xl font-semibold text-white text-base inline-flex items-center justify-center gap-2 disabled:opacity-60"
          style={{ backgroundColor: '#7A1F2B' }}
          data-testid={isSyncing ? 'gate-sync-refresh-btn' : 'gate-view-plans-btn'}
        >
          {isSyncing && refreshing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> {T.sync}
            </>
          ) : (
            primaryBtnLabel
          )}
        </button>

        {/* Apple Guideline 3.1.1 — message neutre en plus du bouton (le bouton
            mène à /pricing qui est lui-même neutralisé en mode magasin).
            🍎 Masqué en mode synchronisation: le user vient de payer, on ne
            doit JAMAIS lui dire "subscriptions not available". */}
        {isStoreApp() && !isSyncing && (
          <div
            className="text-xs mt-3 px-3 py-2 rounded-lg"
            style={{ backgroundColor: '#FAF7F2', color: '#6B7280', border: '1px solid #E5DCC9' }}
            data-testid="gate-store-notice"
          >
            {lang === 'en'
              ? 'Subscriptions are not available from this app.'
              : 'Les abonnements ne sont pas disponibles depuis cette app.'}
          </div>
        )}

        {showRefresh && (
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="w-full mt-2 py-3 rounded-xl font-semibold text-sm inline-flex items-center justify-center gap-2"
            style={{ backgroundColor: '#F5EFE0', color: '#7A1F2B' }}
            data-testid="gate-refresh-btn"
          >
            {refreshing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> {T.sync}
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" /> {T.already_paid}
              </>
            )}
          </button>
        )}

        <button
          onClick={() => navigate('/profile')}
          className="w-full mt-2 py-2.5 text-sm font-medium"
          style={{ color: '#6B7280' }}
          data-testid="gate-profile-btn"
        >
          {T.go_profile}
        </button>

        {/* 🍎 Apple Guideline 3.1.2(c) — disclosure IAP + liens légaux fonctionnels.
            Affichés sur l'écran "essai expiré" / "subscription required" pour
            que les reviewers Apple voient les conditions AVANT d'arriver à
            la page Pricing. Indispensable au compte de revue "expired trial". */}
        <p
          className="text-xs mt-5 leading-relaxed"
          style={{ color: '#6B7280' }}
          data-testid="gate-iap-disclosure"
        >
          {T.iap_disclosure}
        </p>
        <div
          className="mt-3 pt-3 border-t flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs font-semibold"
          style={{ borderColor: '#E5DCC9' }}
        >
          <a
            href="/privacy"
            onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}
            className="underline underline-offset-4"
            style={{ color: '#7A1F2B' }}
            data-testid="gate-privacy-policy-link"
          >
            {T.legal_privacy}
          </a>
          <a
            href="/terms"
            onClick={(e) => { e.preventDefault(); navigate('/terms'); }}
            className="underline underline-offset-4"
            style={{ color: '#7A1F2B' }}
            data-testid="gate-terms-of-use-link"
          >
            {T.legal_terms}
          </a>
        </div>
      </div>
    </div>
  );
}
